import React, { useState } from 'react';
import { FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import type { ShareValidationRequest } from '../types';

interface PasswordGateProps {
  randomId: string;
  linkUuid: string;
  viewerType?: string;
  onValidated: (shareId: string, viewerType?: string, accessCode?: string) => void;
  onError: (error: string) => void;
}

/**
 * Password authentication component for secure report access
 *  Psikosen's  like a secure access control system
 */
const PasswordGate: React.FC<PasswordGateProps> = ({
  randomId,
  linkUuid,
  viewerType,
  onValidated,
  onError,
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);

  const MAX_ATTEMPTS = 5;
  const LOCKOUT_DURATION = 300000; // 5 minutes

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLocked) {
      onError('Too many failed attempts. Please try again later.');
      return;
    }

    // Sanitize common copy/paste artifacts (spaces, NBSP, hyphens)
    const sanitized = password.replace(/[\s\u00A0-]/g, '');
    if (!sanitized) {
      onError('Please enter a password');
      return;
    }

    setIsValidating(true);

    try {
      // Import api service dynamically to avoid circular dependencies
      const { validateShareAccess } = await import('../services/api');
      
      const validationRequest: ShareValidationRequest = {
        randomId,
        linkUuid,
        password: sanitized,
        viewerType: viewerType,
      };

      const response = await validateShareAccess(validationRequest);

      if (response.isValid && response.shareId) {
        // Reset attempts on successful validation
        setAttempts(0);
        onValidated(response.shareId, response.viewerType || viewerType, sanitized);
      } else {
        // Increment failed attempts
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);

        if (newAttempts >= MAX_ATTEMPTS) {
          setIsLocked(true);
          setTimeout(() => {
            setIsLocked(false);
            setAttempts(0);
          }, LOCKOUT_DURATION);
          onError(`Too many failed attempts. Please try again in 5 minutes.`);
        } else {
          onError(response.errorMessage || 'Invalid password. Please try again.');
        }
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Failed to validate access');
    } finally {
      setIsValidating(false);
      setPassword('');
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{
        background: 'linear-gradient(to right, rgb(255, 230, 230), rgb(255, 250, 225), rgb(225, 245, 255))'
      }}
    >
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex flex-col items-center">
            <img 
              src="/asset/mamabear_black.png"
              alt="MamaBear Health"
              className="h-80 w-auto"
            />
            <img 
              src="/asset/mama_origin.png"
              alt=""
              className="h-32 w-32 -mt-16 rounded-full object-cover"
            />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Secure Report Access
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Enter the code provided in your email to view the report
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div className="relative">
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="off"
                  required
                  disabled={isValidating || isLocked}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none rounded-md relative block w-full px-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter code"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <FaEyeSlash className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <FaEye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {attempts > 0 && attempts < MAX_ATTEMPTS && (
            <p className="text-sm text-red-600 text-center">
              {MAX_ATTEMPTS - attempts} attempt{MAX_ATTEMPTS - attempts !== 1 ? 's' : ''} remaining
            </p>
          )}

          <div>
            <button
              type="submit"
              disabled={isValidating || isLocked}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-full text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isValidating ? (
                <>
                  <span className="loading-spinner mr-2"></span>
                  Validating...
                </>
              ) : isLocked ? (
                'Access Temporarily Locked'
              ) : (
                'Access Report'
              )}
            </button>
          </div>

          <div className="text-center space-y-2">
            <p className="text-xs text-gray-500">
              The code was provided in your email
            </p>
            <p className="text-xs text-gray-500">
              Having trouble? Contact{' '}
              <a
                href="mailto:mamabear@loonmd.com"
                className="text-blue-600 hover:text-blue-500"
              >
                mamabear@loonmd.com
              </a>
            </p>
          </div>
        </form>

        <div className="mt-6 border-t border-gray-200 pt-6">
          <div className="text-center">
            <p className="text-xs text-gray-500">
              This is a secure health report containing protected health information.
              Access is logged for security purposes.
            </p>
          </div>
        </div> 
      </div>
    </div>
  );
};

export default PasswordGate;
